import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Shield, Heart, FileText, Money } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";

const PAGES = {
  terms: {
    title: "Terms of Service",
    icon: FileText,
    body: `## Welcome to MangoCubs

These Terms govern your use of the MangoCubs Preschool OS platform. By accessing or using the service, you agree to these terms.

## Eligibility
You must be 18+ and authorised on behalf of your child or organisation to enrol in our services.

## Acceptable Use
- Do not share login credentials.
- Do not upload illegal, harmful, or copyrighted content without permission.
- Respect the privacy of children, families, and staff.

## Service Availability
MangoCubs makes reasonable efforts to keep the platform online 24/7 but does not guarantee uninterrupted access. Scheduled maintenance is announced in advance.

## Termination
We may suspend accounts found in breach of these terms after due notice.

## Governing Law
These terms are governed by the laws of the user's branch country (India for Indian branches, Singapore for SG, etc.).

## Contact
WhatsApp: +91 98441 39333 · Email: mangocubs.edu@gmail.com`,
  },
  privacy: {
    title: "Privacy Policy",
    icon: Shield,
    body: `## Our Privacy Promise

MangoCubs Preschool OS takes child privacy seriously. We follow industry best practices and the COPPA / IT Act / Singapore PDPA standards as applicable.

## What we collect
- Account info: name, email, phone, address.
- Child info: name, date of birth, photo, medical alerts, attendance, daily updates.
- Operational data: payments, communications, app activity.

## How we use it
- To deliver the preschool service (attendance, communication, fees, learning).
- To send safety notifications and updates only to authorised guardians.
- To improve our app — never to sell or share with advertisers.

## Photo & video usage
- Photos uploaded by teachers are reviewed and tagged before being shared with the relevant parent(s) only.
- We use AI to describe photo contents — we DO NOT store biometric face data.
- Parents may withdraw photo consent at any time via Settings.

## Data retention
Records are kept for the duration of enrolment plus 3 years (statutory) or until consent is withdrawn.

## Your rights
- Access, correct, or delete your data via your Settings.
- Request data export at mangocubs.edu@gmail.com.

## Contact
WhatsApp: +91 98441 39333 · Email: mangocubs.edu@gmail.com`,
  },
  refund: {
    title: "Refund Policy",
    icon: Money,
    body: `## Fees & Refunds

We aim to be fair and transparent.

## Tuition fees
- Quarterly fees are non-refundable once the quarter has begun.
- Annual fees are refundable on a pro-rated basis upon withdrawal, less an admin charge of 5%.
- Admission fee is non-refundable.

## Apparel & Materials
- Returns accepted within 7 days of delivery, unworn and tagged.
- Custom-stitched uniforms are non-refundable.

## Activities & Camps
- Withdrawals 14+ days before start: full refund less 10% processing.
- Within 7 days: 50% refund.
- After start: non-refundable.

## How to request a refund
Email mangocubs.edu@gmail.com with your child's name, branch, and reason. Refunds processed within 7 business days.

## Contact
WhatsApp: +91 98441 39333 · Email: mangocubs.edu@gmail.com`,
  },
  "child-privacy": {
    title: "Child Data Protection",
    icon: Heart,
    body: `## Special protections for children

Children deserve extra care. Beyond our general privacy policy, the following applies for everyone under 13:

## Consent
- We only enrol children with verified parent or legal-guardian consent.
- Parents may withdraw consent at any time and ask for full data deletion.

## Minimised data
- We collect the minimum required: name, DOB, medical alerts, attendance, photos for parent communication.
- We never sell child data, never serve ads, and never share with marketers.

## Photo & video safeguards
- Only authorised teachers can upload photos.
- AI describes the photo only — no facial recognition stored.
- An admin or teacher manually confirms which child is in each photo before the photo is shared with parents.
- Only the confirmed child's parents are notified.

## Background checks
- All staff with classroom access undergo background verification.
- Volunteers and vendors are escorted at all times.

## Reporting concerns
Email mangocubs.edu@gmail.com or WhatsApp +91 98441 39333. We take every report seriously and respond within 24 hours.`,
  },
};

export default function CompliancePage({ slug = "terms" }) {
  const page = PAGES[slug] || PAGES.terms;
  const Icon = page.icon;

  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="border-b border-border bg-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2.5">
            <Logo size={40} />
          </Link>
          <Link to="/" className="ml-auto text-sm text-ink-soft flex items-center gap-1 hover:text-ink">
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </header>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <span className="w-12 h-12 rounded-2xl bg-terracotta-soft text-terracotta flex items-center justify-center">
            <Icon size={24} weight="duotone" />
          </span>
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-mustard-hover">Compliance</div>
            <h1 className="font-heading font-bold text-3xl">{page.title}</h1>
          </div>
        </div>
        <div className="mt-8 max-w-none text-ink leading-relaxed" data-testid={`legal-body-${slug}`}>
          {page.body.split("\n").map((line, i) => {
            if (line.startsWith("## ")) return <h2 key={i} className="font-heading font-bold text-xl mt-6 mb-2">{line.slice(3)}</h2>;
            if (line.startsWith("# ")) return <h1 key={i} className="font-heading font-bold text-2xl mt-6 mb-2">{line.slice(2)}</h1>;
            if (line.startsWith("- ")) return <li key={i} className="ml-5 list-disc text-sm">{line.slice(2)}</li>;
            if (!line.trim()) return <div key={i} className="h-2" />;
            return <p key={i} className="text-sm mb-1">{line}</p>;
          })}
        </div>
        <div className="mt-12 pt-6 border-t border-border text-xs text-ink-soft">
          Last updated: February 2026 · For questions, WhatsApp <a className="text-terracotta font-semibold" href="https://wa.me/919844139333">+91 98441 39333</a> or email <a className="text-terracotta font-semibold" href="mailto:mangocubs.edu@gmail.com">mangocubs.edu@gmail.com</a>.
        </div>
      </motion.div>
    </div>
  );
}
